/**
 * Welcome Screen — Entry point for GazeWeave.
 * Load content via paste, URL, EPUB file, or demo.
 */

import { useState, useCallback, useRef } from 'react'
import { useStore } from '@/store'
import { parseText, fetchURL, getSampleDocument } from '@/lib/content-loader'
import { loadEpub } from '@/lib/epub-loader'

export function WelcomeScreen() {
  const setDocument = useStore((s) => s.setDocument)
  const [textInput, setTextInput] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [inputMode, setInputMode] = useState<'paste' | 'url' | 'epub'>('paste')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleLoadDemo = () => setDocument(getSampleDocument())

  const handleLoadText = () => {
    if (!textInput.trim()) return
    setDocument(parseText(textInput, 'paste'))
  }

  const handleLoadURL = async () => {
    if (!urlInput.trim()) return
    setLoading(true)
    setError(null)
    try {
      const doc = await fetchURL(urlInput.trim())
      if (doc.paragraphs.length === 0) {
        setError('Could not extract readable content from this URL.')
        return
      }
      setDocument(doc)
    } catch (e) {
      setError(`Failed to load URL: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleEpubFile = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.epub')) {
      setError('Please select an EPUB file (.epub)')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const doc = await loadEpub(file)
      setDocument(doc)
    } catch (e) {
      setError(`Failed to load EPUB: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }, [setDocument])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleEpubFile(file)
  }, [handleEpubFile])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleEpubFile(file)
  }, [handleEpubFile])

  const canSubmit = inputMode === 'paste' ? textInput.trim().length > 0
    : inputMode === 'url' ? urlInput.trim().length > 0
    : false
  const handleSubmit = inputMode === 'paste' ? handleLoadText : handleLoadURL

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', padding: '40px', gap: '24px',
        transition: 'background 0.2s',
        background: dragOver ? 'rgba(56,189,248,0.05)' : 'transparent',
      }}
    >
      {/* Drag overlay */}
      {dragOver && (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'rgba(15,23,42,0.9)',
          zIndex: 1000, pointerEvents: 'none',
        }}>
          <div style={{
            padding: '40px 60px', borderRadius: '20px',
            border: '3px dashed #38bdf8', textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📚</div>
            <p style={{ fontSize: '20px', color: '#38bdf8', fontWeight: 600 }}>
              Drop EPUB file here
            </p>
          </div>
        </div>
      )}

      {/* Logo */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{
          fontSize: '48px', fontWeight: 700,
          background: 'linear-gradient(135deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          marginBottom: '12px',
        }}>
          GazeWeave
        </h1>
        <p style={{ fontSize: '18px', color: '#94a3b8', maxWidth: '500px' }}>
          Read with your eyes. Text that responds to your gaze — highlighting,
          reflowing, and adapting in real time.
        </p>
      </div>

      {/* Input */}
      <div style={{ width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: '4px', background: '#1e293b', borderRadius: '8px', padding: '3px' }}>
          {(['paste', 'url', 'epub'] as const).map((mode) => (
            <button key={mode} onClick={() => { setInputMode(mode); setError(null) }} style={{
              flex: 1, padding: '8px 12px', borderRadius: '6px', border: 'none',
              background: inputMode === mode ? '#334155' : 'transparent',
              color: inputMode === mode ? '#e2e8f0' : '#64748b',
              cursor: 'pointer', fontSize: '14px', fontWeight: 500,
              transition: 'all 0.15s',
            }}>
              {mode === 'paste' ? '📝 Text' : mode === 'url' ? '🔗 URL' : '📚 EPUB'}
            </button>
          ))}
        </div>

        {/* Input area */}
        {inputMode === 'paste' && (
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste an article, essay, or any text here..."
            style={{
              width: '100%', height: '180px', padding: '16px',
              borderRadius: '12px', border: '1px solid #334155',
              background: '#1e293b', color: '#e2e8f0', fontSize: '15px',
              fontFamily: 'Inter, system-ui, sans-serif',
              resize: 'vertical', outline: 'none',
            }}
          />
        )}

        {inputMode === 'url' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="url" value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setError(null) }}
              placeholder="https://example.com/article"
              onKeyDown={(e) => { if (e.key === 'Enter') handleLoadURL() }}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '12px',
                border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0',
                fontSize: '15px', fontFamily: 'Inter, system-ui, sans-serif', outline: 'none',
              }}
            />
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Paste any article URL — we'll extract the readable content.
            </p>
          </div>
        )}

        {inputMode === 'epub' && (
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '40px', borderRadius: '12px',
              border: '2px dashed #334155', background: '#1e293b',
              textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#38bdf8')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#334155')}
          >
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>📚</div>
            <p style={{ fontSize: '16px', color: '#94a3b8', margin: 0 }}>
              Click to select an EPUB file
            </p>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
              or drag & drop anywhere on this page
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".epub"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Error */}
        {error && <p style={{ fontSize: '14px', color: '#f87171', margin: 0 }}>{error}</p>}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {inputMode !== 'epub' && (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              style={{
                flex: 1, padding: '12px 24px', borderRadius: '10px', border: 'none',
                background: canSubmit && !loading
                  ? 'linear-gradient(135deg, #38bdf8, #818cf8)' : '#334155',
                color: canSubmit && !loading ? '#fff' : '#64748b',
                fontSize: '16px', fontWeight: 600, cursor: canSubmit ? 'pointer' : 'default',
              }}
            >
              {loading ? '⏳ Loading...' : 'Start Reading →'}
            </button>
          )}
          <button onClick={handleLoadDemo} style={{
            flex: inputMode === 'epub' ? 1 : undefined,
            padding: '12px 24px', borderRadius: '10px',
            border: '1px solid #334155', background: 'transparent',
            color: '#94a3b8', fontSize: '16px', cursor: 'pointer',
          }}>
            Try Demo
          </button>
        </div>
      </div>

      {/* Footer */}
      <p style={{ fontSize: '13px', color: '#475569', marginTop: '16px', textAlign: 'center' }}>
        🔒 Privacy-first: webcam data never leaves your device. Mouse tracking as fallback.
      </p>
    </div>
  )
}
